import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import { ApiError, describeApiFieldError } from "./http";
import { loadScenarioSchema } from "./loadDemandContract";
import {
  REQUIRED_PV_CHOICES,
  pvBatteryConfigurationSchema,
  pvInverterRedundancyModeSchema,
  pvPhaseConfigurationSchema,
  pvSizingStatusSchema,
  pvSizingWarningCodeSchema,
  pvSystemTypeSchema,
} from "./pvSizingContract";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

// A factor left out means NOT ESTABLISHED, never 1: the engine sizes with 1,
// names the factor in a warning and reports REVIEW_REQUIRED (Master Prompt
// A16 (a), A19 (h)). The form must omit a blank field instead of sending "1".
const optionalFactor = exactDecimalSchema.optional();

/** The same sentence the Transformer, DG and UPS rating schedules use. */
const RATINGS_ASCENDING = "Ratings must be unique and in ascending order.";

export const pvSizingRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    required_ac_output_kw: exactDecimalSchema,
    // Module data from the project design basis: required, never defaulted (A19 (g)).
    module_rated_power_wp: exactDecimalSchema,
    module_open_circuit_voltage_v: exactDecimalSchema,
    module_maximum_power_voltage_v: exactDecimalSchema,
    module_short_circuit_current_a: exactDecimalSchema,
    temperature_coefficient_voc_percent_per_c: exactDecimalSchema,
    temperature_coefficient_vmp_percent_per_c: exactDecimalSchema,
    minimum_design_temperature_c: exactDecimalSchema,
    maximum_cell_temperature_c: exactDecimalSchema,
    // The inverter window from the project design basis: required (A19 (g)).
    inverter_max_dc_voltage_v: exactDecimalSchema,
    inverter_mppt_min_voltage_v: exactDecimalSchema,
    inverter_mppt_max_voltage_v: exactDecimalSchema,
    inverter_max_input_current_per_mppt_a: exactDecimalSchema,
    mppt_count: z.number().int().positive(),
    maximum_strings_per_mppt: z.number().int().positive(),
    // The design-basis DC/AC ratio: required, reported without limits (A19 (d), (i)).
    target_dc_ac_ratio: exactDecimalSchema,
    available_inverter_ratings_kw: z.array(exactDecimalSchema).min(1),
    ac_efficiency_factor: optionalFactor,
    future_growth_factor: optionalFactor,
    design_margin_factor: optionalFactor,
    ambient_derating_factor: optionalFactor,
    altitude_derating_factor: optionalFactor,
    duty_inverters: z.number().int().positive().optional(),
    redundant_inverters: z.number().int().nonnegative().optional(),
    // The engineer's choice; the backend imposes no default. Optional here only so
    // a missing choice is reported by the rule below as a missing value.
    redundancy_mode: pvInverterRedundancyModeSchema.optional(),
    // Descriptors: optional, no default, echoed only (A19 (j)).
    system_type: pvSystemTypeSchema.optional(),
    phase_configuration: pvPhaseConfigurationSchema.optional(),
    battery_configuration: pvBatteryConfigurationSchema.optional(),
    scenario: loadScenarioSchema.optional(),
    // Echoed only, with a warning when given (A19 (k)); left out means not stated.
    export_limit_kw: exactDecimalSchema.optional(),
    dg_coexistence: z.boolean().optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
    // The PV source of the revision's diagram this study sizes (A28 (d)); left out when
    // none is chosen, so a study that does not use the link is sent as before.
    sld_element_code: z.string().trim().min(1).max(50).optional(),
  })
  .strict()
  .superRefine((values, ctx) => {
    // Mirrors the backend rule (schemas/pv_sizing.py): the schedule must be
    // unique and ascending. Only the first offending entry is flagged, so the
    // message points at one place on screen; an entry that is blank or not a
    // plain decimal already carries its own issue.
    let previous: number | null = null;
    for (const [index, raw] of values.available_inverter_ratings_kw.entries()) {
      if (!exactDecimalSchema.safeParse(raw).success) {
        continue;
      }
      const current = Number(raw);
      if (previous !== null && !(current > previous)) {
        ctx.addIssue({
          code: "custom",
          message: RATINGS_ASCENDING,
          path: ["available_inverter_ratings_kw", index],
        });
        break;
      }
      previous = current;
    }

    for (const field of REQUIRED_PV_CHOICES) {
      if (values[field] === undefined) {
        // Reported as a missing value rather than an unknown option, so the form's
        // describeValidationIssue turns it into "... is required." - the same way
        // the DG and UPS forms report a missing choice.
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
  });

export const pvSizingWarningSchema = z
  .object({
    code: pvSizingWarningCodeSchema,
    message: z.string(),
  })
  .strict();

export const pvSizingResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    // Descriptors come back as given, null when not declared.
    scenario: loadScenarioSchema.nullable(),
    system_type: pvSystemTypeSchema.nullable(),
    phase_configuration: pvPhaseConfigurationSchema.nullable(),
    battery_configuration: pvBatteryConfigurationSchema.nullable(),
    redundancy_mode: pvInverterRedundancyModeSchema,
    required_ac_output_kw: z.string(),
    // The factor the sizing used; "1" when it was not established.
    future_growth_factor: z.string(),
    design_margin_factor: z.string(),
    ac_efficiency_factor: z.string(),
    design_ac_output_kw: z.string(),
    inverter_ac_requirement_kw: z.string(),
    // The two derating factors the sizing used; "1" when one was not established.
    ambient_derating_factor: z.string(),
    altitude_derating_factor: z.string(),
    combined_derating_factor: z.string(),
    derated_inverter_requirement_kw: z.string(),
    duty_inverters: z.number().int(),
    redundant_inverters: z.number().int(),
    total_inverters: z.number().int(),
    required_unit_rating_kw: z.string(),
    // The schedule the selection was made from, echoed in the order it was sent.
    available_inverter_ratings_kw: z.array(exactDecimalSchema).min(1),
    // Null together with the six capacity fields below when nothing could be built
    // (NO_SOLUTION), whichever limit failed.
    selected_unit_rating_kw: z.string().nullable(),
    target_dc_ac_ratio: z.string(),
    required_dc_array_capacity_kwp: z.string(),
    module_rated_power_wp: z.string(),
    // Echoed from the study as sent: the module data, site temperatures and inverter
    // window the string screening used.
    module_open_circuit_voltage_v: z.string(),
    module_maximum_power_voltage_v: z.string(),
    module_short_circuit_current_a: z.string(),
    temperature_coefficient_voc_percent_per_c: z.string(),
    temperature_coefficient_vmp_percent_per_c: z.string(),
    minimum_design_temperature_c: z.string(),
    maximum_cell_temperature_c: z.string(),
    inverter_max_dc_voltage_v: z.string(),
    inverter_mppt_min_voltage_v: z.string(),
    inverter_mppt_max_voltage_v: z.string(),
    inverter_max_input_current_per_mppt_a: z.string(),
    mppt_count: z.number().int(),
    maximum_strings_per_mppt: z.number().int(),
    modules_required: z.number().int(),
    cold_corrected_module_voc_v: z.string(),
    cold_corrected_module_vmp_v: z.string(),
    hot_corrected_module_vmp_v: z.string(),
    maximum_modules_per_string: z.number().int(),
    minimum_modules_per_string: z.number().int(),
    // The string arrangement: null together when the string-length window is empty.
    modules_per_string: z.number().int().nullable(),
    total_strings: z.number().int().nullable(),
    installed_modules: z.number().int().nullable(),
    installed_dc_array_capacity_kwp: z.string().nullable(),
    strings_per_mppt: z.number().int().nullable(),
    mppt_input_current_a: z.string().nullable(),
    cold_string_voc_v: z.string().nullable(),
    installed_duty_capacity_kw: z.string().nullable(),
    total_installed_capacity_kw: z.string().nullable(),
    derated_duty_capacity_kw: z.string().nullable(),
    spare_derated_capacity_kw: z.string().nullable(),
    ac_loading_percent: z.string().nullable(),
    installed_dc_ac_ratio: z.string().nullable(),
    export_limit_kw: z.string().nullable(),
    dg_coexistence: z.boolean().nullable(),
    status: pvSizingStatusSchema,
    warnings: z.array(pvSizingWarningSchema),
    jurisdiction_profile: jurisdictionProfileSchema,
  })
  .strict();

export const pvRunCreateRequestSchema = z
  .object({
    study: pvSizingRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const pvRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: pvSizingResponseSchema,
  })
  .strict();

export type PVSizingRequest = z.infer<typeof pvSizingRequestSchema>;
export type PVSizingWarning = z.infer<typeof pvSizingWarningSchema>;
export type PVSizingResponse = z.infer<typeof pvSizingResponseSchema>;
export type PVRunCreateRequest = z.infer<typeof pvRunCreateRequestSchema>;
export type PVRunResponse = z.infer<typeof pvRunResponseSchema>;

function formatApiError(data: unknown, status: number): string {
  return describeApiFieldError(data, status, "PV sizing failed");
}

/**
 * Calculate a Solar PV sizing study and persist it as a run revision.
 *
 * The request carries the whole run body, because a study's notes belong to the
 * run, not to the sizing the engine calculates. The returned summary carries the
 * run ID, engine version, profile, reference status and content hash shown in
 * the traceability panel.
 *
 * With a project revision id the run is stored under that revision of the selected
 * project; without one it belongs to no project. An unchanged study returns the
 * stored revision with HTTP 200 instead of a new one (A11); both are valid here.
 */
export async function createPVRun(
  payload: PVRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<PVRunResponse> {
  const validatedPayload = pvRunCreateRequestSchema.parse(payload);
  const timeoutSignal = AbortSignal.timeout(30_000);
  const requestSignal = signal
    ? AbortSignal.any([signal, timeoutSignal])
    : timeoutSignal;

  const response = await fetch("/api/v1/electrical/pv-sizing/runs", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal,
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status), response.status);
  }

  const parsed = pvRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS PV runs API.");
  }

  return parsed.data;
}
